import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { layoutPreview, useApp, useSession, useUi } from '../app/context';
import { patchUi } from '../app/ui';
import type { Rect, Size } from '../core/geometry';
import { arrange, layoutFits, layoutRect, readingOrder, type Arrangement, type WindowLayout } from '../core/layouts';
import { isMac } from './keys';
import { Popover } from './Popover';
import { UndoIcon } from './icons';

/**
 * Window Layout: a small surface of arrangements for MyTXT's own windows on
 * its own canvas. Every tile is a miniature of the actual result — drawn from
 * the same geometry that will place the window — and hovering a tile shows
 * that result on the canvas before anything moves.
 */

interface Tile {
  id: WindowLayout;
  label: string;
  /** Shortcut, written as Ctrl-Alt-key (macOS-only letters marked). */
  key?: string;
  macOnly?: boolean;
}

/** Five columns: halves and maximise; quarters and almost-maximise; thirds, centre and the original size. */
const WINDOW_TILES: Tile[] = [
  { id: 'left', label: 'Left Half', key: '←' },
  { id: 'right', label: 'Right Half', key: '→' },
  { id: 'top', label: 'Top Half', key: '↑' },
  { id: 'bottom', label: 'Bottom Half', key: '↓' },
  { id: 'max', label: 'Maximize', key: '↵' },
  { id: 'tl', label: 'Top Left Quarter', key: 'U', macOnly: true },
  { id: 'tr', label: 'Top Right Quarter', key: 'I', macOnly: true },
  { id: 'bl', label: 'Bottom Left Quarter', key: 'J', macOnly: true },
  { id: 'br', label: 'Bottom Right Quarter', key: 'K', macOnly: true },
  { id: 'almost', label: 'Almost Maximize', key: 'E', macOnly: true },
  { id: 'third1', label: 'First Third', key: 'D', macOnly: true },
  { id: 'third2', label: 'Middle Third', key: 'F', macOnly: true },
  { id: 'third3', label: 'Last Third', key: 'G', macOnly: true },
  { id: 'center', label: 'Center', key: 'C', macOnly: true },
  { id: 'default', label: 'Original Size, Centered' },
];

const ARRANGEMENTS: Array<{ id: Arrangement; label: string; hint: string }> = [
  { id: 'auto', label: 'Arrange', hint: 'Share the canvas evenly' },
  { id: 'columns', label: 'Columns', hint: 'Side by side' },
  { id: 'grid', label: 'Grid', hint: 'Rows and columns' },
  { id: 'cascade', label: 'Cascade', hint: 'Overlapping, every head visible' },
];

const keyLabel = (key: string) => (isMac ? `⌃⌥${key}` : `Ctrl+Alt+${key === '⌫' ? 'Backspace' : key}`);

/** A miniature of the canvas with the given windows filled, the focused one strongest. */
export function LayoutGlyph({ rects, canvas, strong = 0 }: { rects: Rect[]; canvas: Size; strong?: number }) {
  const W = 30;
  const H = 21;
  const sx = (W - 4) / canvas.w;
  const sy = (H - 4) / canvas.h;
  return (
    <svg className="layout-glyph" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false">
      <rect className="layout-glyph-frame" x="0.75" y="0.75" width={W - 1.5} height={H - 1.5} rx="4" />
      {rects.map((r, i) => (
        <rect
          key={i}
          className="layout-glyph-win"
          data-strong={i === strong || undefined}
          x={2 + r.x * sx + 0.35}
          y={2 + r.y * sy + 0.35}
          width={Math.max(1, r.w * sx - 0.7)}
          height={Math.max(1, r.h * sy - 0.7)}
          rx="1.6"
        />
      ))}
    </svg>
  );
}

export function LayoutPopover() {
  const id = useUi((s) => s.layoutFor);
  const [last, setLast] = useState(id);
  if (id && id !== last) setLast(id);
  const shown = id ?? last;
  const anchor = useMemo(
    () => (shown ? (document.querySelector<HTMLElement>(`.head[data-window="${shown}"] .wc-btn[data-kind="layout"]`) ?? document.querySelector<HTMLElement>(`.head[data-window="${shown}"] .settings-btn`)) : null),
    // Re-resolve when the surface opens for a window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shown, id],
  );
  const close = () => {
    layoutPreview.set(null);
    patchUi({ layoutFor: null });
  };
  return (
    <Popover open={!!id} anchor={anchor} placement="below-start" onClose={close} label="Window layout" className="layout-popover" grow>
      {shown && <LayoutSurface windowId={shown} onDone={close} />}
    </Popover>
  );
}

function LayoutSurface({ windowId, onDone }: { windowId: string; onDone: () => void }) {
  const app = useApp();
  const windows = useSession((s) => s.windows);
  const prefs = useUi((s) => s.prefs);
  const win = windows.find((w) => w.id === windowId);
  const [caption, setCaption] = useState<{ label: string; key?: string } | null>(null);
  const canvas = app.canvas();
  if (!win) return null;

  const ordered = readingOrder(windows);
  const focusIndex = ordered.findIndex((w) => w.id === windowId);

  const preview = (rects: Rect[] | null, label?: { label: string; key?: string }) => {
    layoutPreview.set(rects);
    setCaption(label ?? null);
  };
  const apply = (fn: () => void) => () => {
    layoutPreview.set(null);
    fn();
    onDone();
    app.focusEditor(windowId);
  };

  const floating = win.zone ? (win.restore ?? win.rect) : win.rect;

  return (
    <div className="layout" onKeyDown={gridKeys} onPointerLeave={() => preview(null)}>
      <div className="layout-heading">
        <span>This Window</span>
      </div>
      <div className="layout-tiles" role="group" aria-label="Layouts for this window">
        {WINDOW_TILES.map((t) => {
          const rect = layoutRect(t.id, canvas, floating);
          const fits = layoutFits(t.id, canvas);
          const active = t.id === win.zone;
          const key = t.key && (!t.macOnly || isMac) ? keyLabel(t.key) : undefined;
          const label = active && t.id !== 'max' ? `${t.label} — again to restore` : t.label;
          return (
            <TileButton
              key={t.id}
              label={t.label}
              active={active}
              disabled={!fits}
              onPreview={() => preview([rect], { label, key })}
              onSelect={apply(() => (t.id === 'max' ? app.toggleMaximize(windowId) : app.layoutWindow(windowId, t.id)))}
            >
              <LayoutGlyph rects={[rect]} canvas={canvas} />
            </TileButton>
          );
        })}
      </div>
      <div className="layout-heading">
        <span>All Windows</span>
        <span className="layout-count">{windows.length}</span>
      </div>
      <div className="layout-tiles layout-tiles-wide" role="group" aria-label="Arrange all windows">
        {ARRANGEMENTS.map((a) => {
          const rects = arrange(windows.length, canvas, a.id);
          return (
            <TileButton key={a.id} label={`${a.label} All Windows`} wide onPreview={() => preview(rects, { label: `${a.label} — ${a.hint}` })} onSelect={apply(() => app.arrangeWindows(a.id))}>
              <LayoutGlyph rects={rects} canvas={canvas} strong={focusIndex} />
              <span className="layout-tile-label">{a.label}</span>
            </TileButton>
          );
        })}
      </div>
      <div className="layout-rows">
        <button
          type="button"
          className="layout-row"
          disabled={windows.length < 2}
          onPointerEnter={() => preview(windows.filter((w) => w.id !== windowId).map((w) => ({ ...w.rect, w: win.rect.w, h: win.rect.h })), { label: 'Every window takes this window’s size' })}
          onClick={apply(() => app.matchWindowSizes(windowId))}
        >
          Match Size to This Window
        </button>
        {app.canUndoLayout() && (
          <button type="button" className="layout-row" onPointerEnter={() => preview(null)} onClick={apply(() => app.undoLayout())}>
            <UndoIcon size={15} />
            Undo Arrangement
            <kbd>{keyLabel('⌫')}</kbd>
          </button>
        )}
        <label className="layout-row layout-switch-row">
          <span>
            Auto Arrange
            <span className="layout-row-note">New and closed windows re-arrange the canvas</span>
          </span>
          <input
            type="checkbox"
            role="switch"
            className="layout-switch"
            checked={prefs.autoArrange}
            onChange={(e) => {
              app.setPrefs({ autoArrange: e.target.checked });
              if (e.target.checked) app.arrangeWindows('auto');
            }}
          />
        </label>
      </div>
      <p className="layout-caption" aria-live="polite">
        {caption ? (
          <>
            <span>{caption.label}</span>
            {caption.key && <kbd>{caption.key}</kbd>}
          </>
        ) : (
          <span className="layout-caption-idle">Point at a layout to preview it</span>
        )}
      </p>
    </div>
  );
}

function TileButton({ label, active, disabled, wide, onPreview, onSelect, children }: { label: string; active?: boolean; disabled?: boolean; wide?: boolean; onPreview: () => void; onSelect: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      className="layout-tile"
      data-wide={wide || undefined}
      aria-label={label}
      aria-pressed={active || undefined}
      disabled={disabled}
      data-tile
      onPointerEnter={onPreview}
      // Keyboard focus moving between tiles previews; the focus the surface gives its first tile on opening does not.
      onFocus={(e) => e.currentTarget.matches(':focus-visible') && !!e.relatedTarget && !!e.currentTarget.closest('.layout')?.contains(e.relatedTarget as Node) && onPreview()}
      onClick={onSelect}
    >
      {children}
    </button>
  );
}

/** Arrow keys move through the tiles as a grid. */
function gridKeys(e: KeyboardEvent<HTMLElement>) {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
  const tiles = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[data-tile]:not(:disabled), .layout-row:not(:disabled):not(label)'));
  const i = tiles.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return;
  const cols = (el: HTMLElement) => (el.dataset.tile !== undefined && !el.dataset.wide ? 5 : el.dataset.wide ? 4 : 1);
  const step = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' ? -cols(tiles[i]) : cols(tiles[i]);
  e.preventDefault();
  tiles[Math.max(0, Math.min(tiles.length - 1, i + step))]?.focus();
}
